import { useState, useContext } from "react";
import { FileText, Download, ExternalLink, Receipt } from "lucide-react";
import { LoginContext } from "../LoginContext";
import { useTranslation } from "react-i18next";
import useInvoices from "../hooks/useInvoices";
import BillingSkeleton from "./skeletons/BillingSkeleton";

export default function ProfileBilling() {
  const { loginInfo } = useContext(LoginContext);
  const { t } = useTranslation();
  const loggedInUser = loginInfo?.username;
  const [filter, setFilter] = useState("all");

  const { data, isLoading, isError } = useInvoices(loggedInUser);

  if (isLoading) return <BillingSkeleton />;

  if (isError)
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-red-50 dark:bg-red-900/20 p-5 sm:p-8 md:p-10 rounded-[2.5rem] border border-red-100 dark:border-red-800 text-red-700 dark:text-red-300 text-center font-bold">
          {t("profileBilling.errorLoading")}
        </div>
      </div>
    );

  const invoices = data?.invoices || [];
  const filtered =
    filter === "all" ? invoices : invoices.filter((inv) => inv.type === filter);

  const formatDate = (unixTimestamp) => {
    return new Date(unixTimestamp * 1000).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatAmount = (amountCents, currency) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "eur",
    }).format(amountCents / 100);
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "subscription":
        return t("profileBilling.typeSubscription");
      case "one-time":
        return t("profileBilling.typeOneTime");
      case "upgrade":
        return t("profileBilling.typeUpgrade");
      default:
        return t("profileBilling.typeOther");
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "subscription":
        return "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400";
      case "one-time":
        return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400";
      case "upgrade":
        return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400";
      default:
        return "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400";
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Compact Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7]">
          <Receipt size={20} className="sm:hidden" />
          <Receipt size={24} className="hidden sm:block" />
        </div>
        <div>
          <h1 className="text-lg sm:text-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
            {t("profileBilling.title")}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
            {t("profileBilling.subtitle")}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-700 p-5 sm:p-8 md:p-10 transition-colors duration-300">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "subscription", "one-time", "upgrade"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                filter === f
                  ? "bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              {f === "all"
                ? t("profileBilling.filterAll")
                : getTypeLabel(f)}
            </button>
          ))}
        </div>

        {/* Invoices List */}
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileText
              size={48}
              className="mx-auto text-slate-300 dark:text-slate-600 mb-4"
            />
            <p className="text-slate-500 dark:text-slate-400 font-bold">
              {t("profileBilling.noInvoices")}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((invoice) => (
              <div
                key={invoice.id}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors gap-3"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-white dark:bg-slate-700 rounded-lg flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-600">
                    <FileText
                      size={18}
                      className="text-[#0f7699] dark:text-[#4fc3f7]"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800 dark:text-white truncate">
                        {invoice.number ||
                          t("profileBilling.noInvoiceNumber")}
                      </span>
                      <span
                        className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${getTypeBadgeColor(
                          invoice.type
                        )}`}
                      >
                        {getTypeLabel(invoice.type)}
                      </span>
                      {invoice.legacy && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                          {t("profileBilling.legacy")}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                      {invoice.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0 w-full sm:w-auto justify-between sm:justify-end">
                  <div className="text-right">
                    <p className="text-sm font-black text-slate-800 dark:text-white">
                      {formatAmount(invoice.amount_paid, invoice.currency)}
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold">
                      {formatDate(invoice.created)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {invoice.invoice_pdf ? (
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 text-[#0f7699] dark:text-[#4fc3f7] hover:bg-[#0f7699]/20 dark:hover:bg-[#4fc3f7]/20 transition-colors"
                        title={t("profileBilling.downloadPdf")}
                      >
                        <Download size={16} />
                      </a>
                    ) : (
                      <div
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-300 dark:text-slate-600"
                        title={t("profileBilling.noPdf")}
                      >
                        <Download size={16} />
                      </div>
                    )}
                    {invoice.hosted_invoice_url && (
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                        title={t("profileBilling.viewOnline")}
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
